import { useGetProfileImageQuery } from '@/app/(protected)/profile/api/ProfileApi';
import HelpWidget from '@/components/HelpWidget';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import ProfileImage from '@/components/ProfileImage';
import StatusWidget from '@/components/StatusWidget';
import { Layout1ConfigDefaultsType } from '@/components/theme-layouts/layout1/Layout1Config';
import useUser from '@auth/useUser';
import useFuseLayoutSettings from '@fuse/core/FuseLayout/useFuseLayoutSettings';
import { useToolbarTheme } from '@fuse/core/FuseSettings/hooks/fuseThemeHooks';
import { Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import { ThemeProvider } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import { twMerge } from 'tailwind-merge';
import _ from 'lodash';
import { memo } from 'react';
import { Link } from 'react-router';
import LightDarkModeToggle from 'src/components/LightDarkModeToggle';
import NavbarToggleButton from 'src/components/theme-layouts/components/navbar/NavbarToggleButton';
import themeOptions from 'src/configs/themeOptions';
import { useCommandPalette } from '@/components/command/CommandPaletteContext';
import useThemeMediaQuery from '../../../../@fuse/hooks/useThemeMediaQuery';
import FullScreenToggle from '../../components/FullScreenToggle';
import { getNestedBoolean, useConfig } from '@/utils/configHook';
import { CommandPaletteButton } from '@/components/command';

type ToolbarLayout1Props = {
	className?: string;
};

/**
 * The toolbar layout 1.
 */
function ToolbarLayout1(props: Readonly<ToolbarLayout1Props>) {
	const { className } = props;

	const settings = useFuseLayoutSettings();
	const config = settings.config as Layout1ConfigDefaultsType;
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
	const { data: user } = useUser();
	const { data: image } = useGetProfileImageQuery(undefined, { skip: !user });
	const { openCommandPalette } = useCommandPalette();

	const toolbarTheme = useToolbarTheme();

	return (
		<ThemeProvider theme={toolbarTheme}>
			<AppBar
				id="fuse-toolbar"
				className={twMerge('relative z-20 flex border-b', className)}
				color="default"
				sx={(theme) => ({
					backgroundColor: toolbarTheme.palette.background.default,
					...theme.applyStyles('light', {
						backgroundColor: toolbarTheme.palette.background.paper
					})
				})}
				position="static"
				elevation={0}
			>
				<Toolbar className="min-h-48 p-0 md:min-h-64">
					<div className="flex flex-1 px-8 md:px-16 space-x-8 items-center">
						{isMobile && <NavbarToggleButton className="mx-0 h-40 w-40 p-0 sm:mx-8" />}

						{(config.toolbar?.showTitle || (!isMobile && config.toolbar?.showCrumbs)) && (
							<div>
								{config.toolbar.showTitle && (
									<Typography
										variant="h1"
										sx={{ fontSize: '2rem', fontWeight: 'bold' }}
									>
										{config.toolbar.title}
									</Typography>
								)}
								{!isMobile && config.toolbar.showCrumbs && (
									<PageBreadcrumb skipHome={config.toolbar.ignoreHome} />
								)}
							</div>
						)}
					</div>

					<div className="flex items-center overflow-x-auto px-8 md:px-16 space-x-5">
						{/* {!isMobile && showHelp && <HelpWidget />} */}
						<HelpWidget />
						{!isMobile && <StatusWidget />}
						<CommandPaletteButton onOpen={openCommandPalette} />
						{!isMobile && <FullScreenToggle />}
						<LightDarkModeToggle
							lightTheme={_.find(themeOptions, { id: 'Default' })}
							darkTheme={_.find(themeOptions, { id: 'Default Dark' })}
						/>
						<Link to="/profile" className='no-underline'>
							<ProfileImage photo={image} user={user} />
						</Link>
					</div>

				</Toolbar>
			</AppBar>
		</ThemeProvider>
	);
}

export default memo(ToolbarLayout1);
